import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, TimelineChronologyType, TimelineChronologyTypeKeys } from '@/store';
import { ChronologyUndoRedoHistory, TimelineUndoRedoHistory } from '@/api';

export const ActiveBundlePageType = {
  LIGHT: 'light',
  FULL: 'full',
} as const;

export type ActiveBundlePageTypeKeys = (typeof ActiveBundlePageType)[keyof typeof ActiveBundlePageType];

export const BUNDLE_PAGE_TYPE_STORAGE_KEY = 'mermaidd_bundle_page_type';

export const getStoredBundlePageType = (): ActiveBundlePageTypeKeys => {
  const stored = localStorage.getItem(BUNDLE_PAGE_TYPE_STORAGE_KEY);
  return stored === ActiveBundlePageType.FULL ? ActiveBundlePageType.FULL : ActiveBundlePageType.LIGHT;
};

interface TimelineChronologyState {
  data: TimelineChronologyData | null;
  activeEntryIndex: number | null;
  selectedEntryIds: string[];
  activeBundlePageType: ActiveBundlePageTypeKeys;
  timelineChronologyUndoRedoHistory: TimelineUndoRedoHistory | ChronologyUndoRedoHistory;
  showDateBar: boolean;
}

interface TimelineChronologyData {
  startDate: string | null;
  endDate: string | null;
  fullBundlePages?: number | null;
  lightBundlePages?: number | null;
  fullBundleId?: string | null;
  lightBundleId?: string | null;
  entries: (string | TimelineChronologyEntry | null | undefined)[] | null;
}

export interface TimelineChronologyEntry {
  id: string;
  date: string | null;
  providerId: string | null;
  providerName: string | null;
  summary: string | null;
  comment: string | null;
  time: string | null;
  pages: TimelineChronologyPage[];
  containsAdditionalRecords?: boolean;
}

export interface TimelineChronologyPage {
  pageId: string;
  fullBundlePageNumber?: number | null;
  lightBundlePageNumber?: number | null;
}

const initialState: TimelineChronologyState = {
  data: null,
  activeEntryIndex: null,
  selectedEntryIds: [],
  activeBundlePageType: getStoredBundlePageType(),
  timelineChronologyUndoRedoHistory: {
    undone: 0,
    applied: 0,
  },
  showDateBar: true,
};

const timelineChronologySlice = createSlice({
  name: 'timelineChronology',
  initialState: {
    [TimelineChronologyType.TIMELINE]: initialState,
    [TimelineChronologyType.CHRONOLOGY]: initialState,
  } as Record<TimelineChronologyTypeKeys, TimelineChronologyState>,
  reducers: {
    setTimelineChronologyData: (
      state,
      action: PayloadAction<{
        key: TimelineChronologyTypeKeys;
        data: TimelineChronologyData;
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].data = data;
    },
    setTimelineChronologyActiveEntryIndex: (
      state,
      action: PayloadAction<{
        key: TimelineChronologyTypeKeys;
        data: number | null;
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].activeEntryIndex = data;
    },
    setTimelineChronologySelectedEntryIds: (
      state,
      action: PayloadAction<{
        key: TimelineChronologyTypeKeys;
        data: string[];
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].selectedEntryIds = data;
    },
    setActiveBundlePageType: (
      state,
      action: PayloadAction<{
        key: TimelineChronologyTypeKeys;
        data: ActiveBundlePageTypeKeys;
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].activeBundlePageType = data;
    },
    setTimelineChronologyUndoRedoHistory: (
      state,
      action: PayloadAction<{
        key: TimelineChronologyTypeKeys;
        data: TimelineUndoRedoHistory | ChronologyUndoRedoHistory;
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].timelineChronologyUndoRedoHistory = data;
    },
    setShowDateBar: (state, action: PayloadAction<{ key: TimelineChronologyTypeKeys; data: boolean }>) => {
      const { key, data } = action.payload;
      state[key].showDateBar = data;
    },
  },
});

export const {
  setTimelineChronologyData,
  setTimelineChronologyActiveEntryIndex,
  setTimelineChronologySelectedEntryIds,
  setActiveBundlePageType,
  setTimelineChronologyUndoRedoHistory,
  setShowDateBar,
} = timelineChronologySlice.actions;

export const selectTimelineChronologyData = (state: RootState) => {
  const activeTimelineChronologyType = state.sharedData.activeTimelineChronologyType;
  return activeTimelineChronologyType ? state.timelineChronology[activeTimelineChronologyType].data : initialState.data;
};

/**
 * with key
 * const timelineChronologyData = useAppSelector(selectTimelineChronologyDataByStoreKey(TimelineChronologyType.CHRONOLOGY));
 * without key
 * const timelineChronologyDataDefault = useAppSelector(selectTimelineChronologyDataByStoreKey());
 */
export const selectTimelineChronologyDataByStoreKey =
  (timelineChronologyKey?: TimelineChronologyTypeKeys) => (state: RootState) => {
    const resolvedTimelineChronologyKey = timelineChronologyKey ?? state.sharedData.activeTimelineChronologyType;
    return resolvedTimelineChronologyKey
      ? state.timelineChronology[resolvedTimelineChronologyKey].data
      : initialState.data;
  };

export const selectTimelineChronologyActiveEntryIndex = (state: RootState) => {
  const activeTimelineChronologyType = state.sharedData.activeTimelineChronologyType;
  return activeTimelineChronologyType
    ? state.timelineChronology[activeTimelineChronologyType].activeEntryIndex
    : initialState.activeEntryIndex;
};

/**
 * with key
 * const timelineChronologyActiveEntryIndex = useAppSelector(selectTimelineChronologyActiveEntryIndexByStoreKey(TimelineChronologyType.CHRONOLOGY));
 * without key
 * const timelineChronologyActiveEntryIndexDefault = useAppSelector(selectTimelineChronologyActiveEntryIndexByStoreKey());
 */
export const selectTimelineChronologyActiveEntryIndexByStoreKey =
  (timelineChronologyKey?: TimelineChronologyTypeKeys) => (state: RootState) => {
    const resolvedTimelineChronologyKey = timelineChronologyKey ?? state.sharedData.activeTimelineChronologyType;
    return resolvedTimelineChronologyKey
      ? state.timelineChronology[resolvedTimelineChronologyKey].activeEntryIndex
      : initialState.activeEntryIndex;
  };

export const selectTimelineChronologySelectedEntryIds = (state: RootState) => {
  const activeTimelineChronologyType = state.sharedData.activeTimelineChronologyType;
  return activeTimelineChronologyType
    ? state.timelineChronology[activeTimelineChronologyType].selectedEntryIds
    : initialState.selectedEntryIds;
};

/**
 * with key
 * const timelineChronologySelectedEntryIds = useAppSelector(selectTimelineChronologySelectedEntryIdsByStoreKey(TimelineChronologyType.CHRONOLOGY));
 * without key
 * const timelineChronologySelectedEntryIdsDefault = useAppSelector(selectTimelineChronologySelectedEntryIdsByStoreKey());
 */
export const selectTimelineChronologySelectedEntryIdsByStoreKey =
  (timelineChronologyKey?: TimelineChronologyTypeKeys) => (state: RootState) => {
    const resolvedTimelineChronologyKey = timelineChronologyKey ?? state.sharedData.activeTimelineChronologyType;
    return resolvedTimelineChronologyKey
      ? state.timelineChronology[resolvedTimelineChronologyKey].selectedEntryIds
      : initialState.selectedEntryIds;
  };

/**
 * without key
 * const activeBundlePageType = useAppSelector(selectActiveBundlePageType);
 */
export const selectActiveBundlePageType = (state: RootState) => {
  const activeTimelineChronologyType = state.sharedData.activeTimelineChronologyType;
  return activeTimelineChronologyType
    ? state.timelineChronology[activeTimelineChronologyType].activeBundlePageType
    : initialState.activeBundlePageType;
};

export const selectTimelineChronologyUndoRedoHistory = (state: RootState) => {
  const activeTimelineChronologyType = state.sharedData.activeTimelineChronologyType;
  return activeTimelineChronologyType
    ? state.timelineChronology[activeTimelineChronologyType].timelineChronologyUndoRedoHistory
    : initialState.timelineChronologyUndoRedoHistory;
};

/**
 * without key
 * const showDateBar = useAppSelector(selectShowDateBar);
 */
export const selectShowDateBar = (state: RootState) => {
  const activeTimelineChronologyType = state.sharedData.activeTimelineChronologyType;
  return activeTimelineChronologyType
    ? state.timelineChronology[activeTimelineChronologyType].showDateBar
    : initialState.showDateBar;
};

export const timelineChronologyReducer = timelineChronologySlice.reducer;
